/**
 * How the sign-in window tells the page that started it that an attempt settled. The window is
 * opened with `noopener`, so there is no `window.opener` to post to; a same-origin
 * BroadcastChannel carries the message instead, and no provider navigation can sever it. Polling
 * stays the fallback, and the only path when the sign-in finished in another browser.
 */
export type AttemptMessage = {
  type: "abacor:connection-attempt";
  attemptId: string;
  status: string;
};
type Ack = { type: "abacor:connection-attempt-ack"; attemptId: string };

const CHANNEL = "abacor:connections";

export function isAttemptMessage(value: unknown, attemptId: string): value is AttemptMessage {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { type?: unknown }).type === "abacor:connection-attempt" &&
    (value as { attemptId?: unknown }).attemptId === attemptId
  );
}

const channel = () =>
  typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL) : null;

/**
 * Listens for one attempt and acknowledges it, so the sign-in window knows a page heard it and
 * can close instead of opening the app a second time.
 */
export function listenForAttempt(attemptId: string, onSettled: () => void) {
  const broadcast = channel();
  if (!broadcast) return () => {};
  broadcast.onmessage = (event: MessageEvent) => {
    if (!isAttemptMessage(event.data, attemptId)) return;
    broadcast.postMessage({ type: "abacor:connection-attempt-ack", attemptId } satisfies Ack);
    onSettled();
  };
  return () => broadcast.close();
}

/**
 * Sends the settled attempt to whichever page is waiting for it. Resolves true only when one
 * acknowledges within `waitMs`.
 */
export async function announceAttempt(message: AttemptMessage, waitMs = 800): Promise<boolean> {
  const broadcast = channel();
  if (!broadcast) return false;
  try {
    return await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), waitMs);
      broadcast.onmessage = (event: MessageEvent) => {
        const ack = event.data as Partial<Ack> | null;
        if (ack?.type === "abacor:connection-attempt-ack" && ack.attemptId === message.attemptId) {
          clearTimeout(timer);
          resolve(true);
        }
      };
      broadcast.postMessage(message);
    });
  } finally {
    broadcast.close();
  }
}
